import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ConfigProvider, message, Row, Select, Switch } from 'antd';
import type {
  OperatingHours,
  SpecialHours,
  TimeRange,
  Weekday,
} from '@/apis/types';
import { AddItemButton, CustomButton, CustomTitle } from '@/components';
import { DeleteIcon, PencilIcon } from '@/assets/icons/icons';
import TimeRangeList from './TimeRangeList';
import SpecialHoursEditor from './SpecialHoursEditor';
import {
  DEFAULT_RANGE,
  MAX_SPECIAL_HOURS,
  WEEKDAYS,
  describeSpecialHours,
  isWeekdays8To6,
  rangesError,
  sortRanges,
  timeZoneOptions,
  todayIn,
  weekdays8To6,
} from './operatingHours';
import formStyles from '../LocationForm.module.scss';
import styles from './OperatingHours.module.scss';

interface EditOperatingHoursPanelProps {
  initial: OperatingHours;
  /** Time zone detected from the location's address, if known. */
  detectedTimezone: string | null;
  /** True before the location's first save (its zone isn't detected yet). */
  isNewLocation?: boolean;
  /** Whether the location has coordinates to detect a time zone from. */
  hasCoordinates?: boolean;
  /** Disables Save while the parent persists the hours. */
  saving?: boolean;
  onSave: (hours: OperatingHours) => void;
  onCancel: () => void;
}

const timezoneHelp = (
  timezone: string | null,
  detected: string | null,
  isNewLocation: boolean,
  hasCoordinates: boolean
): string => {
  if (detected) {
    return timezone === null
      ? 'Detected from address'
      : `Address time zone is ${detected}`;
  }
  if (timezone) return 'Selected manually';
  if (isNewLocation)
    return 'Detected from the address once the location is saved';
  return hasCoordinates
    ? 'Detected from the address when you save'
    : 'No address time zone yet. Pick one to show open or closed status.';
};

// A symbol, so it can never equal a stored holiday id (ids are strings).
const NEW_ENTRY = Symbol('new special hours');

const newSpecialHours = (timezone: string | null): SpecialHours => ({
  id:
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `special-${Date.now()}`,
  name: '',
  start_date: todayIn(timezone),
  end_date: null,
  closed: true,
  hours: [],
  repeats_yearly: false,
});

/** "Edit operating hours" view of the location drawer. */
const EditOperatingHoursPanel: React.FC<EditOperatingHoursPanelProps> = ({
  initial,
  detectedTimezone,
  isNewLocation = false,
  hasCoordinates = false,
  saving = false,
  onSave,
  onCancel,
}) => {
  const [hours, setHours] = useState<OperatingHours>(initial);
  const [mode, setMode] = useState<'preset' | 'custom'>(
    isWeekdays8To6(initial.weekly) ? 'preset' : 'custom'
  );
  const [editingId, setEditingId] = useState<string | typeof NEW_ENTRY | null>(
    null
  );
  const [newEntry, setNewEntry] = useState<SpecialHours | null>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // data-focus-key of the control to focus once the holiday editor closes.
  const [pendingFocus, setPendingFocus] = useState<string | null>(null);

  // The form view hides the Edit button that had focus; start here instead.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // Closing the holiday editor removes the control that had focus; return it
  // to the entry's Edit button or "Add date" (the heading if neither shows).
  useEffect(() => {
    if (!pendingFocus || editingId !== null) return;
    const byKey = (key: string) =>
      Array.from(
        bodyRef.current?.querySelectorAll<HTMLElement>('[data-focus-key]') ?? []
      ).find((el) => el.dataset.focusKey === key);
    const target = byKey(pendingFocus) ?? byKey('add-date');
    (target ?? headingRef.current)?.focus();
    setPendingFocus(null);
  }, [pendingFocus, editingId]);

  const closeEditor = (focusKey: string) => {
    setEditingId(null);
    setNewEntry(null);
    setPendingFocus(focusKey);
  };

  const effectiveTimezone = hours.timezone ?? detectedTimezone;
  const zones = useMemo(
    () =>
      timeZoneOptions(effectiveTimezone).map((zone) => ({
        value: zone,
        label: zone,
      })),
    [effectiveTimezone]
  );

  // While the parent saves, the draft is locked (controls are disabled too):
  // a successful save closes the panel and would drop later edits.
  const setDay = (day: Weekday, ranges: TimeRange[]) => {
    if (saving) return;
    setHours((h) => ({ ...h, weekly: { ...h.weekly, [day]: ranges } }));
    setMode('custom');
  };

  const applyPreset = () => {
    if (saving) return;
    setHours((h) => ({ ...h, weekly: weekdays8To6() }));
    setMode('preset');
  };

  const saveSpecial = (entry: SpecialHours) => {
    if (saving) return;
    setHours((h) => ({
      ...h,
      special_hours:
        editingId === NEW_ENTRY
          ? [...h.special_hours, entry]
          : h.special_hours.map((s) => (s.id === entry.id ? entry : s)),
    }));
    closeEditor(`edit:${entry.id}`);
  };

  const deleteSpecial = (id: string) => {
    if (saving) return;
    setHours((h) => ({
      ...h,
      special_hours: h.special_hours.filter((s) => s.id !== id),
    }));
    closeEditor('add-date');
  };

  const handleSave = () => {
    if (saving) return;
    if (editingId) {
      message.error('Save or cancel the special hours you are editing first.');
      return;
    }
    const invalidDay = WEEKDAYS.find(({ key }) =>
      rangesError(hours.weekly[key])
    );
    if (invalidDay) {
      message.error(`Fix the hours for ${invalidDay.label}.`);
      return;
    }
    const weekly = { ...hours.weekly };
    WEEKDAYS.forEach(({ key }) => {
      weekly[key] = sortRanges(weekly[key]);
    });
    onSave({ ...hours, weekly });
  };

  return (
    <div className={styles.panel}>
      <div
        className={`${formStyles.stickyHeader} ${styles.focusTarget}`}
        ref={headingRef}
        tabIndex={-1}
      >
        <CustomTitle
          text='Edit operating hours'
          fontSize={20}
          fontWeight='700'
          lineHeight='32px'
          color='var(--text-primary)'
          marginBottom={0}
        />
      </div>

      <div
        className={`custom-scrollbar ${formStyles.scrollBody}`}
        ref={bodyRef}
      >
        <ConfigProvider componentDisabled={saving}>
          <fieldset className={styles.lockable} disabled={saving}>
            <label
              className={styles.fieldLabel}
              htmlFor='operating-hours-timezone'
            >
              Time zone
            </label>
            <Select
              id='operating-hours-timezone'
              variant='filled'
              className={styles.filledControl}
              showSearch
              value={effectiveTimezone ?? undefined}
              placeholder='Select time zone'
              options={zones}
              onChange={(zone: string) => {
                if (saving) return;
                setHours((h) => ({
                  ...h,
                  // Null keeps the hours following the address's time zone.
                  timezone: zone === detectedTimezone ? null : zone,
                }));
              }}
            />
            <div className={styles.helperText}>
              {timezoneHelp(
                hours.timezone,
                detectedTimezone,
                isNewLocation,
                hasCoordinates
              )}
            </div>

            <div
              className={styles.presets}
              role='group'
              aria-label='Hours preset'
            >
              <button
                type='button'
                className={`${styles.presetChip} ${
                  mode === 'preset' ? styles.presetChipActive : ''
                }`}
                aria-pressed={mode === 'preset'}
                onClick={applyPreset}
              >
                Weekdays 8 to 6
              </button>
              <button
                type='button'
                className={`${styles.presetChip} ${
                  mode === 'custom' ? styles.presetChipActive : ''
                }`}
                aria-pressed={mode === 'custom'}
                onClick={() => {
                  if (!saving) setMode('custom');
                }}
              >
                Custom
              </button>
            </div>

            {WEEKDAYS.map(({ key, label }) => {
              const ranges = hours.weekly[key];
              const open = ranges.length > 0;
              return (
                <div className={styles.dayRow} key={key}>
                  <div className={styles.dayToggle}>
                    <Switch
                      className={styles.daySwitch}
                      checked={open}
                      aria-label={`${label} open`}
                      onChange={(checked) =>
                        setDay(key, checked ? [{ ...DEFAULT_RANGE }] : [])
                      }
                    />
                    {label}
                  </div>
                  {open ? (
                    <TimeRangeList
                      ranges={ranges}
                      label={label}
                      onChange={(next) => setDay(key, next)}
                    />
                  ) : (
                    <span className={styles.closedText}>Closed</span>
                  )}
                </div>
              );
            })}

            <div className={styles.sectionDivider} />
            <div className={styles.sectionTitle}>Holidays & special hours</div>

            {hours.special_hours.map((entry) =>
              editingId === entry.id ? (
                <SpecialHoursEditor
                  key={entry.id}
                  entry={entry}
                  isNew={false}
                  onSave={saveSpecial}
                  onCancel={() => closeEditor(`edit:${entry.id}`)}
                />
              ) : (
                <div className={styles.specialCard} key={entry.id}>
                  <div>
                    <div className={styles.specialName}>{entry.name}</div>
                    <div className={styles.specialMeta}>
                      {describeSpecialHours(entry)}
                    </div>
                  </div>
                  <div className={styles.specialActions}>
                    <button
                      type='button'
                      className={styles.iconButton}
                      aria-label={`Edit ${entry.name}`}
                      data-focus-key={`edit:${entry.id}`}
                      disabled={editingId !== null}
                      onClick={() => setEditingId(entry.id)}
                    >
                      <PencilIcon color='var(--action-icon-color)' />
                    </button>
                    <button
                      type='button'
                      className={styles.iconButton}
                      aria-label={`Delete ${entry.name}`}
                      disabled={editingId !== null}
                      onClick={() => deleteSpecial(entry.id)}
                    >
                      <DeleteIcon color='var(--action-icon-color)' />
                    </button>
                  </div>
                </div>
              )
            )}

            {editingId === NEW_ENTRY && newEntry && (
              <SpecialHoursEditor
                entry={newEntry}
                isNew
                onSave={saveSpecial}
                onCancel={() => closeEditor('add-date')}
              />
            )}

            {editingId === null &&
              hours.special_hours.length >= MAX_SPECIAL_HOURS && (
                <div className={styles.helperText}>
                  Up to {MAX_SPECIAL_HOURS} dates. Delete one to add another.
                </div>
              )}

            {editingId === null &&
              hours.special_hours.length < MAX_SPECIAL_HOURS && (
                // AddItemButton renders a div; the button makes it focusable.
                <button
                  type='button'
                  className={styles.addDate}
                  aria-label='Add date'
                  data-focus-key='add-date'
                  onClick={() => {
                    if (saving) return;
                    setNewEntry(newSpecialHours(effectiveTimezone));
                    setEditingId(NEW_ENTRY);
                  }}
                >
                  <AddItemButton
                    fontSize='12px'
                    marginTop='0px'
                    fontWeight='600'
                    width='28px'
                    height='28px'
                    text='Add date'
                  />
                </button>
              )}
          </fieldset>
        </ConfigProvider>
      </div>

      <div className={formStyles.stickyFooter}>
        <Row gutter={[0, 8]} style={{ marginTop: '0' }}>
          <CustomButton
            text='Save'
            bgColor='#000'
            color='#fff'
            fontSize='16px'
            fontWeight='600'
            boxShadow='none'
            height='48px'
            width='100%'
            borderRadius='8px'
            isFormValid={saving}
            onClick={handleSave}
          />
          <CustomButton
            text='Cancel'
            bgColor='transparent'
            color='var(--text-primary)'
            fontSize='16px'
            padding='11.5px 0px 12px 0px'
            fontWeight='600'
            boxShadow='none'
            height='auto'
            width='100%'
            border='none'
            marginTop='0px'
            isFormValid={saving}
            // Leaving mid-save would drop the draft if the save then fails.
            onClick={() => {
              if (!saving) onCancel();
            }}
          />
        </Row>
      </div>
    </div>
  );
};

export default EditOperatingHoursPanel;
